import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useFormContext } from "react-hook-form";
import { getFromStatisticsAdsCampaign } from "../../../../../services/adsCampaigns";
import { GOOGLE } from "../../../../../utils/constants";
import {
  VALUE_NUMBER,
  VALUE_WITH_PERCENTAGE,
  ADS_CAMPAIGNS_STATISTICS_INSIGHTS_BEST_PERFORMING_AD_GROUP,
  IMPRESSIONS,
  RESULTS,
  CTR,
  CLICKS,
  TOP_KEYWORDS,
  ADS_CAMPAIGNS_STATISTICS_INSIGHTS_BEST_PERFORMING_AD,
  AD,
  ADS_CAMPAIGNS_STATISTICS_INSIGHTS_BEST_PERFORMING_AD_TITLE,
  ADS_CAMPAIGNS_STATISTICS_INSIGHTS_BEST_PERFORMING_AD_DESCRIPTION,
  ADS_CAMPAIGNS_STATISTICS_INSIGHTS_BEST_PERFORMING_ASSETS_GROUP,
  ADS_CAMPAIGNS_STATISTICS_INSIGHTS_TOP_VIDEOS,
  ADS_CAMPAIGNS_STATISTICS_INSIGHTS_TOP_IMAGES,
  ADS_CAMPAIGNS_STATISTICS_INSIGHTS_TOP_HEADLINES,
  ADS_CAMPAIGNS_STATISTICS_INSIGHTS_TOP_LONG_HEADLINES,
  ADS_CAMPAIGNS_STATISTICS_INSIGHTS_TOP_DESCRIPTIONS,
} from "../../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../../lib/i18next";
import { openLink } from "../../../../../utils/string";
import Grid from "@mui/material/Grid";
import React from "react";
import _ from "lodash";
import { Divider } from "@mui/material";
import PerformingAssetsCard from "../components/PerformingAssetsCard";
import CardMedia from "../../../../../components/Media/CardMedia";
import ModalMedia from "../../../../../components/Modal/ModalMedia";

const GooglePerformingAssets = () => {
  const { t } = useTranslationApp();
  const { campaign, campaignID } = useFormContext();
  const { statistics } = getFromStatisticsAdsCampaign(campaign);
  // Returns the best assets group, prioritizing mainAction, otherwise by click
  const getBestAssetsGroup = (assetsGroupsStatistics) => {
    if (!assetsGroupsStatistics) return null;

    let bestByMainAction = null;
    let bestByClick = null;
    let hasMainAction = false;

    Object.entries(assetsGroupsStatistics).forEach(([assetsGroupId, stats]) => {
      if (stats.mainAction && stats.mainAction > 0) {
        hasMainAction = true;
        if (
          !bestByMainAction ||
          stats.mainAction > bestByMainAction.statistics.mainAction
        ) {
          bestByMainAction = {
            assetsGroupId,
            statistics: stats,
          };
        }
      }
      if (
        !bestByClick ||
        (stats.click || 0) > (bestByClick.statistics.click || 0)
      ) {
        bestByClick = {
          assetsGroupId,
          statistics: stats,
        };
      }
    });

    return hasMainAction ? bestByMainAction : bestByClick;
  };

  // Returns the best ad group, prioritizing mainAction, otherwise by click
  const getBestAdGroup = (adGroupsStatistics) => {
    if (!adGroupsStatistics) return null;

    let bestByMainAction = null;
    let bestByClick = null;
    let hasMainAction = false;

    Object.entries(adGroupsStatistics).forEach(([adGroupId, stats]) => {
      if (stats.mainAction && stats.mainAction > 0) {
        hasMainAction = true;
        if (
          !bestByMainAction ||
          stats.mainAction > bestByMainAction.statistics.mainAction
        ) {
          bestByMainAction = {
            adGroupId,
            statistics: stats,
          };
        }
      }
      if (
        !bestByClick ||
        (stats.click || 0) > (bestByClick.statistics.click || 0)
      ) {
        bestByClick = {
          adGroupId,
          statistics: stats,
        };
      }
    });

    return hasMainAction ? bestByMainAction : bestByClick;
  };

  // Returns the top 5 keywords, prioritizing mainAction, otherwise by click
  const getTop5Keywords = (adsGroups, statistics) => {
    if (!adsGroups || !statistics) return [];

    const allKeywords = [];

    adsGroups.forEach((adGroup) => {
      if (adGroup.platform !== GOOGLE) return;

      const adGroupKeywords = statistics[adGroup.id] || {};

      Object.entries(adGroupKeywords).forEach(([keywordId, keywordStats]) => {
        const keywordIndex = adGroup.keywordsIDs.indexOf(keywordId);
        if (keywordIndex === -1) return;

        allKeywords.push({
          adGroupId: adGroup.id,
          keywordId,
          keywordText: adGroup.keywords[keywordIndex],
          statistics: keywordStats,
        });
      });
    });

    // Check if at least one keyword has mainAction > 0
    const hasMainAction = allKeywords.some(
      (k) => k.statistics.mainAction && k.statistics.mainAction > 0
    );

    if (hasMainAction) {
      return allKeywords
        .sort(
          (a, b) =>
            (b.statistics.mainAction || 0) - (a.statistics.mainAction || 0)
        )
        .slice(0, 5);
    } else {
      return allKeywords
        .sort((a, b) => (b.statistics.click || 0) - (a.statistics.click || 0))
        .slice(0, 5);
    }
  };

  // Returns the top 5 videos, prioritizing mainAction, otherwise by click
  const getTop5Videos = (videosStatistics) => {
    if (!videosStatistics) return [];

    const videosMap = new Map();

    // Iterate over all assetsGroups and videos
    Object.entries(videosStatistics).forEach(([, videos]) => {
      Object.entries(videos).forEach(([, videoStats]) => {
        const youtubeVideoID = videoStats.youtubeVideoID;
        if (!youtubeVideoID) return;

        // Group videos by youtubeVideoID and sum statistics
        if (videosMap.has(youtubeVideoID)) {
          const existingVideo = videosMap.get(youtubeVideoID);
          existingVideo.statistics.impressions =
            (existingVideo.statistics.impressions || 0) +
            (videoStats.impressions || 0);
          existingVideo.statistics.click =
            (existingVideo.statistics.click || 0) + (videoStats.click || 0);
          existingVideo.statistics.mainAction =
            (existingVideo.statistics.mainAction || 0) +
            (videoStats.mainAction || 0);
          // Keep the first title found if available
          if (
            !existingVideo.youtubeVideoTitle &&
            videoStats.youtubeVideoTitle
          ) {
            existingVideo.youtubeVideoTitle = videoStats.youtubeVideoTitle;
          }
        } else {
          videosMap.set(youtubeVideoID, {
            youtubeVideoID,
            youtubeVideoTitle: videoStats.youtubeVideoTitle,
            statistics: {
              impressions: videoStats.impressions || 0,
              click: videoStats.click || 0,
              mainAction: videoStats.mainAction || 0,
            },
          });
        }
      });
    });

    const allVideos = Array.from(videosMap.values());

    // Check if at least one video has mainAction > 0
    const hasMainAction = allVideos.some(
      (v) => v.statistics.mainAction && v.statistics.mainAction > 0
    );

    if (hasMainAction) {
      return allVideos
        .sort(
          (a, b) =>
            (b.statistics.mainAction || 0) - (a.statistics.mainAction || 0)
        )
        .slice(0, 5);
    } else {
      return allVideos
        .sort((a, b) => (b.statistics.click || 0) - (a.statistics.click || 0))
        .slice(0, 5);
    }
  };

  // Returns the top 5 images, prioritizing mainAction, otherwise by click
  const getTop5Images = (imagesStatistics) => {
    if (!imagesStatistics) return [];

    const imagesMap = new Map();

    // Iterate over all assetsGroups and images
    Object.entries(imagesStatistics).forEach(([, images]) => {
      Object.entries(images).forEach(([, imageStats]) => {
        const imageUrl = imageStats.imageUrl;
        if (!imageUrl) return;

        // Group images by imageUrl and sum statistics
        if (imagesMap.has(imageUrl)) {
          const existingImage = imagesMap.get(imageUrl);
          existingImage.statistics.impressions =
            (existingImage.statistics.impressions || 0) +
            (imageStats.impressions || 0);
          existingImage.statistics.click =
            (existingImage.statistics.click || 0) + (imageStats.click || 0);
          existingImage.statistics.mainAction =
            (existingImage.statistics.mainAction || 0) +
            (imageStats.mainAction || 0);
        } else {
          imagesMap.set(imageUrl, {
            imageUrl,
            statistics: {
              impressions: imageStats.impressions || 0,
              click: imageStats.click || 0,
              mainAction: imageStats.mainAction || 0,
            },
          });
        }
      });
    });

    const allImages = Array.from(imagesMap.values());

    // Check if at least one image has mainAction > 0
    const hasMainAction = allImages.some(
      (img) => img.statistics.mainAction && img.statistics.mainAction > 0
    );

    if (hasMainAction) {
      return allImages
        .sort(
          (a, b) =>
            (b.statistics.mainAction || 0) - (a.statistics.mainAction || 0)
        )
        .slice(0, 5);
    } else {
      return allImages
        .sort((a, b) => (b.statistics.click || 0) - (a.statistics.click || 0))
        .slice(0, 5);
    }
  };

  // Returns the top 5 text assets (headlines / long headlines / descriptions)
  const getTop5TextAssets = (textStatistics) => {
    if (!textStatistics) return [];

    const textsMap = new Map();

    Object.entries(textStatistics).forEach(([, texts]) => {
      Object.entries(texts).forEach(([assetID, textStats]) => {
        const text = textStats?.text;
        if (!text) return;

        if (textsMap.has(text)) {
          const existing = textsMap.get(text);
          existing.statistics.impressions =
            (existing.statistics.impressions || 0) +
            (textStats.impressions || 0);
          existing.statistics.click =
            (existing.statistics.click || 0) + (textStats.click || 0);
          existing.statistics.mainAction =
            (existing.statistics.mainAction || 0) + (textStats.mainAction || 0);
        } else {
          textsMap.set(text, {
            assetID,
            text,
            statistics: {
              impressions: textStats.impressions || 0,
              click: textStats.click || 0,
              mainAction: textStats.mainAction || 0,
            },
          });
        }
      });
    });

    const allTexts = Array.from(textsMap.values());
    const hasMainAction = allTexts.some(
      (item) => item.statistics.mainAction && item.statistics.mainAction > 0
    );

    if (hasMainAction) {
      return allTexts
        .sort(
          (a, b) =>
            (b.statistics.mainAction || 0) - (a.statistics.mainAction || 0)
        )
        .slice(0, 5);
    }

    return allTexts
      .sort((a, b) => (b.statistics.click || 0) - (a.statistics.click || 0))
      .slice(0, 5);
  };

  // Returns the best ad, prioritizing mainAction, otherwise by click
  const getBestAd = (adsGroups, statistics) => {
    if (!adsGroups || !statistics) return null;

    let bestByMainAction = null;
    let bestByClick = null;
    let hasMainAction = false;

    adsGroups.forEach((adGroup) => {
      if (adGroup.platform !== GOOGLE) return;

      adGroup.ads.forEach((ad) => {
        const adStats = statistics[ad.id];
        if (!adStats) return;

        if (adStats.mainAction && adStats.mainAction > 0) {
          hasMainAction = true;
          if (
            !bestByMainAction ||
            adStats.mainAction > bestByMainAction.statistics.mainAction
          ) {
            bestByMainAction = {
              adGroupId: adGroup.id,
              adId: ad.id,
              statistics: adStats,
            };
          }
        }
        if (
          !bestByClick ||
          (adStats.click || 0) > (bestByClick.statistics.click || 0)
        ) {
          bestByClick = {
            adGroupId: adGroup.id,
            adId: ad.id,
            statistics: adStats,
          };
        }
      });
    });

    return hasMainAction ? bestByMainAction : bestByClick;
  };

  const getBestCopies = (statistics) => {
    if (!statistics) return null;

    let allTitles = [];
    let allDescriptions = [];

    Object.entries(statistics).forEach(([, adTextsStats]) => {
      if (adTextsStats.titles) {
        allTitles.push(...Object.values(adTextsStats.titles));
      }
      if (adTextsStats.descriptions) {
        allDescriptions.push(...Object.values(adTextsStats.descriptions));
      }
    });

    const bestTitle = allTitles.reduce((best, current) => {
      if (!best || current.impressions > best?.statistics?.impressions) {
        return {
          text: current.text,
          statistics: current,
        };
      }
      return best;
    }, null);

    const bestDescription = allDescriptions.reduce((best, current) => {
      if (!best || current.impressions > best?.statistics?.impressions) {
        return {
          text: current.text,
          statistics: current.statistics,
        };
      }
      return best;
    }, null);

    return {
      bestTitle,
      bestDescription,
    };
  };

  const bestAssetsGroup = getBestAssetsGroup(statistics[GOOGLE].assetsGroups);
  const bestAdGroup = getBestAdGroup(statistics[GOOGLE].adsGroups);
  const bestAd = getBestAd(campaign.adsGroups, statistics[GOOGLE].ads);
  const { bestTitle, bestDescription } = getBestCopies(
    statistics[GOOGLE].adTexts
  );
  const top5Keywords = getTop5Keywords(
    campaign.adsGroups,
    statistics[GOOGLE].keywords
  );
  const top5Videos = getTop5Videos(statistics[GOOGLE].videos);
  const top5Images = getTop5Images(statistics[GOOGLE].images);
  const top5Headlines = getTop5TextAssets(
    statistics[GOOGLE].titles || statistics[GOOGLE].assets?.titles
  );
  const top5LongHeadlines = getTop5TextAssets(
    statistics[GOOGLE].longHeadlines || statistics[GOOGLE].assets?.longHeadlines
  );
  const top5Descriptions = getTop5TextAssets(
    statistics[GOOGLE].descriptions || statistics[GOOGLE].assets?.descriptions
  );

  const getAssetsGroupByID = (id) => {
    if (!id) {
      return {};
    }
    const assetsGroup = campaign.assetsGroups.find(
      (assetsGroup) => assetsGroup.id === id
    );
    if (!assetsGroup) {
      return {};
    }
    return { assetsGroup, assetsGroupID: assetsGroup?.id };
  };

  const getAdGroupByID = (id) => {
    if (!id) {
      return {};
    }
    const adGroupGoogle = campaign.adsGroups.filter(
      (adGroup) => adGroup.platform === GOOGLE
    );
    const adGroup = adGroupGoogle.find((adGroup) => adGroup.id === id);

    return { adGroup, adGroupID: adGroup.id };
  };

  const getAdByID = (adId) => {
    if (!adId) {
      return {};
    }
    const adGroupsGoogle = campaign.adsGroups.filter(
      (adGroup) => adGroup.platform === GOOGLE
    );

    const foundAdGroup = adGroupsGoogle.find((adGroup) =>
      adGroup.ads.some((ad) => ad.id === adId)
    );

    if (!foundAdGroup) {
      return {};
    }

    const ad = foundAdGroup.ads.find((ad) => ad.id === adId);
    const indexAd = foundAdGroup.ads.findIndex((ad) => ad.id === adId);
    const adGroupIndex = adGroupsGoogle.findIndex(
      (group) => group.id === foundAdGroup.id
    );
    const adGroupID = foundAdGroup.id;

    return {
      ad,
      adID: ad.id,
      adGroupIndex,
      adGroupID,
      indexAd,
    };
  };

  const { assetsGroup = {}, assetsGroupID } = getAssetsGroupByID(
    bestAssetsGroup?.assetsGroupId
  );
  const { adGroup = {}, adGroupID } = getAdGroupByID(bestAdGroup?.adGroupId);
  const {
    ad = {},
    adID,
    adGroupID: adGroupIDAd,
    indexAd,
  } = getAdByID(bestAd?.adId);

  const bestAssetsGroupStats = !_.isEmpty(bestAssetsGroup)
    ? [
        {
          label: t(IMPRESSIONS),
          value: t(VALUE_NUMBER, {
            value: bestAssetsGroup.statistics.impressions,
          }),
        },
        {
          label: t(CLICKS),
          value: t(VALUE_NUMBER, { value: bestAssetsGroup.statistics.click }),
        },
        {
          label: t(CTR),
          value: t(VALUE_WITH_PERCENTAGE, {
            value: bestAssetsGroup.statistics.ctr,
          }),
        },
        {
          label: t(RESULTS),
          value: t(VALUE_NUMBER, {
            value: bestAssetsGroup.statistics.mainAction,
          }),
        },
      ]
    : [];

  const bestAdGroupStats = !_.isEmpty(bestAdGroup)
    ? [
        {
          label: t(IMPRESSIONS),
          value: t(VALUE_NUMBER, { value: bestAdGroup.statistics.impressions }),
        },
        {
          label: t(CLICKS),
          value: t(VALUE_NUMBER, { value: bestAdGroup.statistics.click }),
        },
        {
          label: t(CTR),
          value: t(VALUE_WITH_PERCENTAGE, {
            value: bestAdGroup.statistics.ctr,
          }),
        },
        {
          label: t(RESULTS),
          value: t(VALUE_NUMBER, { value: bestAdGroup.statistics.mainAction }),
        },
      ]
    : [];

  const bestAdStats = !_.isEmpty(bestAd)
    ? [
        {
          label: t(IMPRESSIONS),
          value: t(VALUE_NUMBER, { value: bestAd.statistics.impressions }),
        },
        {
          label: t(CLICKS),
          value: t(VALUE_NUMBER, { value: bestAd.statistics.click }),
        },
        {
          label: t(CTR),
          value: t(VALUE_WITH_PERCENTAGE, { value: bestAd.statistics.ctr }),
        },
        {
          label: t(RESULTS),
          value: t(VALUE_NUMBER, { value: bestAd.statistics.mainAction }),
        },
      ]
    : [];

  const insightCards = [
    !_.isEmpty(bestAssetsGroup) && (
      <PerformingAssetsCard
        key="bestAssetsGroup"
        platform={GOOGLE}
        title={t(
          ADS_CAMPAIGNS_STATISTICS_INSIGHTS_BEST_PERFORMING_ASSETS_GROUP
        )}
        subtitle={assetsGroup.name}
        onSubtitleClick={() =>
          openLink(
            `ads-campaign?campaignID=${campaignID}&platform=${GOOGLE}&assetsGroupID=${assetsGroupID}`
          )
        }
        statistics={bestAssetsGroupStats}
      />
    ),
    !_.isEmpty(bestAdGroup) && (
      <PerformingAssetsCard
        key="bestAdGroup"
        platform={GOOGLE}
        title={t(ADS_CAMPAIGNS_STATISTICS_INSIGHTS_BEST_PERFORMING_AD_GROUP)}
        subtitle={adGroup.name}
        onSubtitleClick={() =>
          openLink(
            `ads-campaign?campaignID=${campaignID}&platform=${GOOGLE}&adGroupID=${adGroupID}`
          )
        }
        statistics={bestAdGroupStats}
      />
    ),
    !_.isEmpty(top5Keywords) && (
      <PerformingAssetsCard
        key="top5Keywords"
        platform={GOOGLE}
        nextContent={<TopKeywords top5Keywords={top5Keywords} />}
      />
    ),
    !_.isEmpty(top5Images) && (
      <PerformingAssetsCard
        key="top5Images"
        platform={GOOGLE}
        nextContent={<TopImages top5Images={top5Images} />}
      />
    ),
    !_.isEmpty(top5Videos) && (
      <PerformingAssetsCard
        key="top5Videos"
        platform={GOOGLE}
        nextContent={<TopVideos top5Videos={top5Videos} />}
      />
    ),
    !_.isEmpty(top5Headlines) && (
      <PerformingAssetsCard
        key="top5Headlines"
        platform={GOOGLE}
        nextContent={
          <TopTextAssets
            titleKey={ADS_CAMPAIGNS_STATISTICS_INSIGHTS_TOP_HEADLINES}
            top5Texts={top5Headlines}
          />
        }
      />
    ),
    !_.isEmpty(top5LongHeadlines) && (
      <PerformingAssetsCard
        key="top5LongHeadlines"
        platform={GOOGLE}
        nextContent={
          <TopTextAssets
            titleKey={ADS_CAMPAIGNS_STATISTICS_INSIGHTS_TOP_LONG_HEADLINES}
            top5Texts={top5LongHeadlines}
          />
        }
      />
    ),
    !_.isEmpty(top5Descriptions) && (
      <PerformingAssetsCard
        key="top5Descriptions"
        platform={GOOGLE}
        nextContent={
          <TopTextAssets
            titleKey={ADS_CAMPAIGNS_STATISTICS_INSIGHTS_TOP_DESCRIPTIONS}
            top5Texts={top5Descriptions}
          />
        }
      />
    ),
    !_.isEmpty(bestAd) && (
      <PerformingAssetsCard
        key="bestAd"
        platform={GOOGLE}
        title={t(ADS_CAMPAIGNS_STATISTICS_INSIGHTS_BEST_PERFORMING_AD)}
        subtitle={ad.name || `${t(AD)} ${indexAd + 1}`}
        onSubtitleClick={() =>
          openLink(
            `ads-campaign?campaignID=${campaignID}&platform=${GOOGLE}&adGroupID=${adGroupIDAd}&adID=${adID}`
          )
        }
        statistics={bestAdStats}
        nextContent={
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            {bestTitle && (
              <Box>
                <Typography variant="subtitle1" color="fields.placeholder">
                  {t(
                    ADS_CAMPAIGNS_STATISTICS_INSIGHTS_BEST_PERFORMING_AD_TITLE
                  )}
                </Typography>
                <Typography variant="body2">{bestTitle.text}</Typography>
              </Box>
            )}
            {bestDescription && (
              <Box>
                <Typography variant="subtitle1" color="fields.placeholder">
                  {t(
                    ADS_CAMPAIGNS_STATISTICS_INSIGHTS_BEST_PERFORMING_AD_DESCRIPTION
                  )}
                </Typography>
                <Typography variant="body2">{bestDescription.text}</Typography>
              </Box>
            )}
          </Box>
        }
      />
    ),
  ].filter(Boolean);

  const cardCount = insightCards.length;

  const gridContent = (
    <Grid container spacing={2} alignItems="stretch">
      {insightCards.map((card, idx) => {
        const sm = cardCount === 1 ? 12 : 6;

        return (
          <Grid
            item
            xs={12}
            sm={sm}
            key={idx}
            sx={{ display: "flex", width: "100%" }}
          >
            {React.cloneElement(card, {
              sx: { ...card.props.sx, height: "100%", width: "100%" },
            })}
          </Grid>
        );
      })}
    </Grid>
  );

  return gridContent;
};

const TABLE_METRIC_CELL_SX = {
  flex: 2,
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
};

const PerformingAssetsTable = ({ title, metricHeaders = [], children }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      flex: 1,
      height: "100%",
      minHeight: 0,
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexShrink: 0,
        mb: 0.5,
      }}
    >
      <Box sx={{ flex: 6, minWidth: 0 }}>
        <Typography fontSize="1.2rem" variant="subtitle1" fontWeight="600">
          {title}
        </Typography>
      </Box>
      {metricHeaders.map((label) => (
        <Box key={label} sx={TABLE_METRIC_CELL_SX}>
          <Typography
            variant="caption"
            component="p"
            fontWeight="600"
            color="fields.placeholder"
          >
            {label}
          </Typography>
        </Box>
      ))}
    </Box>
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
      }}
    >
      {children}
    </Box>
  </Box>
);

const PerformingAssetsTableRow = ({ children, showDivider = false }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      flex: 1,
      minHeight: 0,
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flex: 1,
        minHeight: 0,
        py: 0.5,
      }}
    >
      {children}
    </Box>
    {showDivider ? <Divider /> : null}
  </Box>
);

const TopKeywords = ({ top5Keywords }) => {
  const { t } = useTranslationApp();
  return (
    <PerformingAssetsTable
      title={t(TOP_KEYWORDS)}
      metricHeaders={[
        t(IMPRESSIONS).slice(0, 4) + ".",
        t(CTR).slice(0, 4),
        t(RESULTS).slice(0, 5) + ".",
      ]}
    >
      {top5Keywords.map((keyword, index) => (
        <PerformingAssetsTableRow
          key={keyword.keywordId}
          showDivider={index < top5Keywords.length - 1}
        >
          <Box sx={{ flex: 6, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                wordBreak: "keep-all",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {keyword.keywordText}
            </Typography>
          </Box>
          <Box sx={TABLE_METRIC_CELL_SX}>
            <Typography variant="body2">
              {t(VALUE_NUMBER, {
                value: keyword.statistics.impressions,
              })}
            </Typography>
          </Box>
          <Box sx={TABLE_METRIC_CELL_SX}>
            <Typography variant="body2">
              {t(VALUE_WITH_PERCENTAGE, {
                value: keyword.statistics.ctr.toFixed(1),
              })}
            </Typography>
          </Box>
          <Box sx={TABLE_METRIC_CELL_SX}>
            <Typography variant="body2">
              {t(VALUE_NUMBER, {
                value: keyword.statistics.mainAction,
              })}
            </Typography>
          </Box>
        </PerformingAssetsTableRow>
      ))}
    </PerformingAssetsTable>
  );
};

const TopVideos = ({ top5Videos }) => {
  const { t } = useTranslationApp();
  return (
    <PerformingAssetsTable
      title={t(ADS_CAMPAIGNS_STATISTICS_INSIGHTS_TOP_VIDEOS)}
      metricHeaders={[
        t(IMPRESSIONS).slice(0, 4) + ".",
        t(CLICKS).slice(0, 5) + ".",
        t(RESULTS).slice(0, 5) + ".",
      ]}
    >
      {top5Videos.map((video, index) => (
        <PerformingAssetsTableRow
          key={video.youtubeVideoID}
          showDivider={index < top5Videos.length - 1}
        >
          <Box sx={{ flex: 6, minWidth: 0 }}>
            <Box
              sx={{
                width: "auto",
                maxWidth: 250,
                height: 120,
                flexShrink: 0,
                borderRadius: 1,
              }}
            >
              <Box
                component="iframe"
                src={`https://www.youtube.com/embed/${video.youtubeVideoID}`}
                title={video.youtubeVideoID}
                sx={{
                  width: "100%",
                  height: "100%",
                  border: 0,
                  display: "block",
                }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </Box>
          </Box>
          <Box sx={TABLE_METRIC_CELL_SX}>
            <Typography variant="body2">
              {t(VALUE_NUMBER, {
                value: video.statistics.impressions,
              })}
            </Typography>
          </Box>
          <Box sx={TABLE_METRIC_CELL_SX}>
            <Typography variant="body2">
              {t(VALUE_NUMBER, {
                value: video.statistics.click,
              })}
            </Typography>
          </Box>
          <Box sx={TABLE_METRIC_CELL_SX}>
            <Typography variant="body2">
              {t(VALUE_NUMBER, {
                value: video.statistics.mainAction,
              })}
            </Typography>
          </Box>
        </PerformingAssetsTableRow>
      ))}
    </PerformingAssetsTable>
  );
};

const TopTextAssets = ({ titleKey, top5Texts }) => {
  const { t } = useTranslationApp();
  return (
    <PerformingAssetsTable
      title={t(titleKey)}
      metricHeaders={[
        t(IMPRESSIONS).slice(0, 4) + ".",
        t(CLICKS).slice(0, 5) + ".",
        t(RESULTS).slice(0, 5) + ".",
      ]}
    >
      {top5Texts.map((item, index) => (
        <PerformingAssetsTableRow
          key={`${item.assetID}-${item.text}`}
          showDivider={index < top5Texts.length - 1}
        >
          <Box sx={{ flex: 6, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                wordBreak: "break-word",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {item.text}
            </Typography>
          </Box>
          <Box sx={TABLE_METRIC_CELL_SX}>
            <Typography variant="body2">
              {t(VALUE_NUMBER, {
                value: item.statistics.impressions,
              })}
            </Typography>
          </Box>
          <Box sx={TABLE_METRIC_CELL_SX}>
            <Typography variant="body2">
              {t(VALUE_NUMBER, {
                value: item.statistics.click,
              })}
            </Typography>
          </Box>
          <Box sx={TABLE_METRIC_CELL_SX}>
            <Typography variant="body2">
              {t(VALUE_NUMBER, {
                value: item.statistics.mainAction,
              })}
            </Typography>
          </Box>
        </PerformingAssetsTableRow>
      ))}
    </PerformingAssetsTable>
  );
};

const TopImages = ({ top5Images }) => {
  const { t } = useTranslationApp();
  const [previewImageUrl, setPreviewImageUrl] = React.useState(null);

  const onImageClick = (imageUrl) => {
    setPreviewImageUrl(imageUrl);
  };

  const onClosePreview = () => {
    setPreviewImageUrl(null);
  };

  return (
    <>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          height: "100%",
        }}
      >
        <PerformingAssetsTable
          title={t(ADS_CAMPAIGNS_STATISTICS_INSIGHTS_TOP_IMAGES)}
          metricHeaders={[
            t(IMPRESSIONS).slice(0, 4) + ".",
            t(CLICKS).slice(0, 5) + ".",
            t(RESULTS).slice(0, 5) + ".",
          ]}
        >
          {top5Images.map((image, index) => (
            <PerformingAssetsTableRow
              key={image.imageUrl}
              showDivider={index < top5Images.length - 1}
            >
              <Box sx={{ flex: 6, minWidth: 0 }}>
                <Box
                  onClick={() => onImageClick(image.imageUrl)}
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: 1,
                    overflow: "hidden",
                    flexShrink: 0,
                    cursor: "pointer",
                    transition: "transform 0.2s ease",
                    "&:hover": {
                      transform: "scale(1.05)",
                    },
                  }}
                >
                  <CardMedia
                    url={image.imageUrl}
                    sxImage={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </Box>
              </Box>
              <Box sx={TABLE_METRIC_CELL_SX}>
                <Typography variant="body2">
                  {t(VALUE_NUMBER, {
                    value: image.statistics.impressions,
                  })}
                </Typography>
              </Box>
              <Box sx={TABLE_METRIC_CELL_SX}>
                <Typography variant="body2">
                  {t(VALUE_NUMBER, {
                    value: image.statistics.click,
                  })}
                </Typography>
              </Box>
              <Box sx={TABLE_METRIC_CELL_SX}>
                <Typography variant="body2">
                  {t(VALUE_NUMBER, {
                    value: image.statistics.mainAction,
                  })}
                </Typography>
              </Box>
            </PerformingAssetsTableRow>
          ))}
        </PerformingAssetsTable>
      </Box>

      {previewImageUrl && (
        <ModalMedia
          modalOpen={!!previewImageUrl}
          onCloseModal={onClosePreview}
          url={previewImageUrl}
          showEditButton={false}
          showShareButton={false}
          showDownloadButton={false}
        />
      )}
    </>
  );
};

export default GooglePerformingAssets;
